import React, { memo, useState } from 'react';
import {
	Control,
	FieldValues,
	UseFormRegister,
	UseFormSetValue,
	UseFormWatch,
} from 'react-hook-form';

import {
	Button,
	DatepickerRange,
	FoldPanel,
} from '@components/common';

import { TSelectOption } from '@interfaces/components/componentTypes';
import { IModalSelectOption, TValueType } from '@components/common/ModalSelect/ModalSelect.types';

import { EParametersFormValues } from '../enums';

import { datePickerErrorText, HISTORY_CHECK_OPTIONS_WIDTH } from '../constants';

import HistoryCheckAwtgRanOptions from './HistoryCheckAwtgRanOptions/HistoryCheckAwtgRanOptions';
import HistoryCheckDefaultOptions from './HistoryCheckDefaultOptions';
import HistoryCheckMavenirOptions from './HistoryCheckMavenirOptions';

import styles from './HistoryCheckOptions.module.scss';

interface HistoryCheckOptionsProps {
	isOpenOptions: boolean;
	toggleOptions: () => void;
	technologiesOptions: TSelectOption[];
	nodeTypesOptions: TSelectOption[];
	versionsOptions: TSelectOption[];
	nodesOptions: TSelectOption<TValueType>[];
	configTypeOptions: TSelectOption[];
	datesOptions: Date[];
	siteIdsOptions: IModalSelectOption[];
	networkElementsOptions: IModalSelectOption[];
	control: Control;
	watch: UseFormWatch<FieldValues>;
	setValue: UseFormSetValue<FieldValues>;
	register: UseFormRegister<FieldValues>;
	handleSubmit: () => void;
	isDisabledSubmit: boolean;
	disableDatepickerRange: boolean;
	isLoading: boolean;
	isMavenir: boolean;
	isAwtgRan: boolean;
}

const HistoryCheckOptions = ({
	isOpenOptions,
	toggleOptions,
	technologiesOptions,
	nodeTypesOptions,
	configTypeOptions,
	nodesOptions,
	versionsOptions,
	datesOptions,
	siteIdsOptions,
	networkElementsOptions,
	control,
	handleSubmit,
	watch,
	setValue,
	isDisabledSubmit,
	disableDatepickerRange,
	isLoading,
	isMavenir,
	isAwtgRan,
}: HistoryCheckOptionsProps) => {
	const [isRangeError, setIsRangeError] = useState(false);

	const fromDate = watch(EParametersFormValues.from);
	const toDate = watch(EParametersFormValues.to);

	const setFromDate = (date: number | null) => setValue(EParametersFormValues.from, date);
	const setToDate = (date: number | null) => setValue(EParametersFormValues.to, date);

	return (
		<FoldPanel
			buttonsComponent={
				<div className={styles.historyCheckOptionsButton}>
					<Button
						disabled={isDisabledSubmit || isRangeError}
						large
						onClick={handleSubmit}
						text={isAwtgRan ? 'Apply' : 'Apply MO Branches'}
					/>
				</div>
			}
			isLoading={isLoading}
			isMinWidth
			isOpen={isOpenOptions}
			title='History Check Options'
			togglePanel={toggleOptions}
			width={HISTORY_CHECK_OPTIONS_WIDTH}
		>
			{
				isMavenir
					? (
						<HistoryCheckMavenirOptions
							configTypeOptions={configTypeOptions}
							control={control}
							nodesOptions={nodesOptions}
							setValue={setValue}
							versionOptions={versionsOptions}
							watch={watch}
						/>
					)
					: isAwtgRan
						? (
							<HistoryCheckAwtgRanOptions
								configTypeOptions={configTypeOptions}
								control={control}
								networkElementsOptions={networkElementsOptions}
								setValue={setValue}
								watch={watch}
							/>
						)
						: (
							<HistoryCheckDefaultOptions
								control={control}
								isLoading={isLoading}
								isMavenir={isMavenir}
								nodeTypesOptions={nodeTypesOptions}
								setValue={setValue}
								siteIdsOptions={siteIdsOptions}
								technologiesOptions={technologiesOptions}
								watch={watch}
							/>
						)

			}
			<div className={styles.datepickerWrapper}>
				<DatepickerRange
					disableInput
					disabled={!datesOptions.length || disableDatepickerRange}
					errorMessage={datePickerErrorText}
					fromLabel='History Source Range from'
					handleChangeFromDate={setFromDate}
					handleChangeToDate={setToDate}
					includeDatesFrom={datesOptions}
					includeDatesTo={datesOptions}
					isMobileDevice={false}
					isSingleErrorMessage
					requiredFromDate
					requiredToDate
					setRangeError={setIsRangeError}
					toLabel='to'
					value={{
						from: fromDate,
						to: toDate,
					}}
				/>
			</div>
		</FoldPanel>
	);
};

export default memo(HistoryCheckOptions);
